import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Map, fromJS } from "immutable";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { Col, Row } from "react-bootstrap";
import { connect } from "react-redux";
import {
  formValueSelector,
  Field,
  Fields,
  FormSection,
  change
} from "redux-form";
import classNames from "classnames";

import api from "APP_ROOT/api";
import {
  form,
  Form as AppForm,
  Errors,
  TextArea,
  Check,
  CheckGroup,
  AutoCompleteField
} from "MODULES/shared/components/Forms";
import Total from "MODULES/IncomingQuotes/Total";
import * as companyActions from "MODULES/companies/actions";
import PartDetails from "MODULES/IncomingQuotes/PartDetails";
import IncomingQuoteTypeSelect from "MODULES/IncomingQuotes/IncomingQuoteTypeSelect";
import FormToggle from "MODULES/companies/shared/FindOrCreateCompanyToggle/FormToggle";
import SelectInventoryTable from "MODULES/OutgoingQuotes/shared/SelectInventoryTable";
import SellableQty from "MODULES/parts/components/shared/SellableQty";
import * as actions from "MODULES/parts/actions";
import * as userActions from "MODULES/users/actions";
import { isLessThan36HoursAgo, monthsAgo } from "LIB/time";
import {
  AWAITING_REPLY,
  NO_QUOTE,
  SEND_QUOTE
} from "MODULES/IncomingQuotes/constants";
import { PART, SELLABLE_QUANTITIES } from "MODULES/parts/constants";
import DoNotDoBusinessWarning from "MODULES/parts/components/Detail/IncomingQuotes/Create/DoNotDoBusinessWarning";
import { EXCHANGE_FEE, QUOTE_TYPE } from "MODULES/OutgoingQuotes/constants";
import {
  COMPANY,
  COMPANY_ID,
  CONTACT_ID,
  COST,
  ID,
  NOTES,
  QUANTITY
} from "APP_ROOT/constants";
import { DO_NOT_LIST, LIST } from "MODULES/inventories/constants";
import { EXTERNAL_NOTE } from "MODULES/PurchaseOrders/constants";
import { properCase } from "LIB/formatters/text";
import { LineItemsTable } from "MODULES/parts/components/Detail/IncomingQuotes/LineItemsTable";
import { WEBSITE } from "MODULES/companies/constants";
import { BUYER_ID } from "MODULES/users/constants";
import { useCurrentUser } from "APP_ROOT/hooks";
import ExistingQuoteWarning from "./ExistingQuoteWarning";
import validate from "./validate";

const formName = "partDetailNewIncomingQuote";

export function Form(props) {
  const {
    part,
    quantity,
    cost,
    quoteType,
    quoteTypes,
    companyId,
    selectedCompany,
    selectedEmail,
    setFieldValue,
    setSelectedCompany,
    setSelectedEmail,
    modal
  } = props;

  const companyQuotes = useCompanyQuotes(part.get(ID), companyId);
  const [selectedBuyer, setSelectedBuyer] = useState(useCurrentUser());

  const recentCompanyQuotes = companyQuotes.filter(q =>
    isLessThan36HoursAgo(q.created_at)
  );
  const filter = useMemo(
    () => ({
      limit: "5",
      sellable_status: [LIST, DO_NOT_LIST].join(",")
    }),
    []
  );
  const partAsJson = useMemo(() => part.toJS(), [part]);

  const notesColumnClassName = classNames({
    "col-md-12": quoteType !== AWAITING_REPLY,
    "col-md-6": quoteType === AWAITING_REPLY
  });

  const onCompanySelected = company => {
    setSelectedCompany(company);
    setFieldValue([COMPANY, WEBSITE].join("."))(
      company ? company.website : null
    );
  };

  return (
    <AppForm {...props} modal={modal}>
      <AppForm.Body>
        <DoNotDoBusinessWarning company={selectedCompany} />
        {recentCompanyQuotes.length > 0 && (
          <ExistingQuoteWarning quote={fromJS(recentCompanyQuotes[0])} />
        )}
        <Row>
          <Col md={12}>
            <FormRow>
              <Col md={6}>
                <FormSection name="incoming_quote_line_item">
                  <Fields
                    fieldName={QUOTE_TYPE}
                    names={[COST, QUANTITY, EXCHANGE_FEE]}
                    component={IncomingQuoteTypeSelect}
                    quoteTypes={quoteTypes}
                    setFieldValue={setFieldValue}
                  />
                </FormSection>
              </Col>
              <Col md={6}>
                <Field
                  name={BUYER_ID}
                  label="Buyer"
                  placeholder="Search by name..."
                  component={AutoCompleteField}
                  search={props.searchBuyers}
                  selectedItem={selectedBuyer}
                  onItemSelected={item => setSelectedBuyer(item)}
                  formatItem={result => result && result.full_name}
                  required
                />
              </Col>
            </FormRow>
            <hr className="hr-xs hr-dashed" />
            <FormToggle
              allowPortalPurchase
              companySearch={actions.searchQuoteCompanies}
              formName={formName}
              handleEmail={setSelectedEmail}
              onCompanySelected={onCompanySelected}
            />
            {quoteType === AWAITING_REPLY && (
              <FormRow>
                <Col md={4}>
                  <CheckGroup checkGroupStyles={{ marginTop: 0 }} justified>
                    <Field
                      name={SEND_QUOTE}
                      component={Check}
                      label="Send RFQ"
                    />
                  </CheckGroup>
                </Col>
              </FormRow>
            )}
            <SellableQty
              quantities={part.get(SELLABLE_QUANTITIES, Map({}))}
              style={{ marginTop: 20 }}
            />
            {companyQuotes &&
              companyQuotes.length > 0 && [
                <h4 className="margin-bottom-30 line-height-2">
                  Most Recent Quotes
                </h4>,
                <div className="reset-panel-table">
                  <LineItemsTable
                    results={companyQuotes}
                    limitResults
                    loading={false}
                    inForm
                  />
                </div>
              ]}
            <h4 className="margin-bottom-30 line-height-2">
              Next Available Inventory
            </h4>
            <div className="row reset-panel-table">
              <FormSection name="ingoing_quote_line_item">
                <SelectInventoryTable
                  filter={filter}
                  part={partAsJson}
                  choosable={false}
                  modal={false}
                  displayQuantity={false}
                />
              </FormSection>
            </div>
            {quoteType !== NO_QUOTE && (
              <div>
                <hr className="hr-sm" />
                <h4>Part Details</h4>
                <FormSection name="incoming_quote_line_item">
                  <PartDetails quoteType={quoteType} part={part} />
                </FormSection>
              </div>
            )}
            <hr className="hr-xs hr-dashed" />
            <FormRow>
              <div className={notesColumnClassName}>
                <div className="form-group">
                  <FormSection name="incoming_quote_line_item">
                    <Field
                      name={NOTES}
                      component={TextArea}
                      label="Note (Applied to the line item)"
                      rows={5}
                    />
                  </FormSection>
                </div>
              </div>
              {quoteType === AWAITING_REPLY && (
                <div className={notesColumnClassName}>
                  <div className="form-group">
                    <FormSection name="incoming_quote_line_item">
                      <Field
                        name={EXTERNAL_NOTE}
                        component={TextArea}
                        label={properCase(EXTERNAL_NOTE)}
                        rows={5}
                      />
                    </FormSection>
                  </div>
                </div>
              )}
            </FormRow>
            {!modal && (
              <div>
                <hr className="hr-xs hr-dashed" />
                <div className="row">
                  <Col md={6}>
                    <Total
                      part={part.toJS()}
                      quantity={quantity}
                      cost={cost}
                      email={selectedEmail}
                    />
                  </Col>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </AppForm.Body>
      <AppForm.Footer>
        {props.submitFailed && <Errors errors={props.errorMessages} />}
        <AppForm.Cancel close={props.close} editable />
        <AppForm.Submit
          disabled={false}
          editable
          submitting={props.submitting}
          onClick={() => props.setSubmittedWith("save")}
        />
        {!modal && (
          <AppForm.Submit
            disabled={false}
            editable
            submitting={props.submitting}
            text="Save and Go To Quote"
            onClick={() => props.setSubmittedWith("save-and-go")}
          />
        )}
      </AppForm.Footer>
    </AppForm>
  );
}

Form.propTypes = {
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  quoteType: PropTypes.string,
  quoteTypes: PropTypes.instanceOf(Object),
  part: PropTypes.instanceOf(Map),
  companyId: PropTypes.number,
  selectedCompany: PropTypes.instanceOf(Object).isRequired,
  selectedEmail: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  setSelectedCompany: PropTypes.func.isRequired,
  setSelectedEmail: PropTypes.func.isRequired
};

const selector = formValueSelector(formName);

export default connect(
  state => ({
    quantity: selector(state, "incoming_quote_line_item.quantity"),
    companyId: selector(state, COMPANY_ID),
    cost: selector(state, "incoming_quote_line_item.cost"),
    quoteType: selector(state, "incoming_quote_line_item.quote_type"),
    part: state.partDetail.get(PART),
    contact: selector(state, CONTACT_ID)
  }),
  dispatch => ({
    searchBuyers: filter => dispatch(userActions.searchUsers(filter)),
    setFieldValue: field => val => dispatch(change(formName, field, val))
  })
)(
  form({
    form: formName,
    validate,
    asyncValidate: companyActions.validateContact,
    asyncBlurFields: ["contact.email"]
  })(Form)
);

// Custom hooks
function useCompanyQuotes(partId, companyId) {
  const [recentCompanyQuotes, setRecentCompanyQuotes] = useState([]);

  useEffect(
    () => {
      if (partId && companyId) {
        api.parts.incomingQuoteLineItems
          .search(partId, {
            limit: 5,
            company_id: companyId,
            created_after: monthsAgo(18)
          })
          .then(res => {
            setRecentCompanyQuotes(res.data.results);
          });
      }

      return function cleanup() {};
    },
    [companyId]
  );

  return recentCompanyQuotes;
}



// WEBPACK FOOTER //
// ./app/modules/parts/components/Detail/IncomingQuotes/Create/Form.jsx